import React, { useMemo } from 'react';
import _ from 'lodash';

import { gql } from '@nerdwallet/apollo';
import { Accent, Stack } from '@nerdwallet/currency';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression as BedrockImpression } from '@nerdwallet/analytics/react/impression';
import { checkComponentShouldTriggerLegacyImpressions } from '~/lib/legacy';

import { getArticleTypeConfig } from '~/lib/article-utils';
import BlockGroup from '../block-group/block-group';

import { RelatedLinksFragment } from '~/generated-gql/generated-types';

interface Props {
  article: RelatedLinksFragment;
  showMore?: boolean;
  numItemsAboveFold?: number;
  maxItems?: number;
}

const RelatedLinks: React.GraphqlFC<Props> = ({
  article,
  showMore,
  numItemsAboveFold,
  maxItems,
}) => {
  const { relatedLinks: blockGroup } = article;

  const shouldTriggerLegacyImpressions = useMemo(
    () => checkComponentShouldTriggerLegacyImpressions('RelatedLinks'),
    []
  );

  const children = (
    <BedrockImpression
      eventType="content_element_impression"
      entityName="related_links"
      payload={{
        content_source: 'wp',
        content_id: parseInt(blockGroup.id, 10),
        content_id_type: 'post',
      }}
    >
      <Stack>
        <Accent size="small">On a similar note...</Accent>
        <BlockGroup
          blockGroup={blockGroup}
          wpPostListLimit={maxItems}
          template={showMore ? 'expandable-list' : 'image-list'}
          templateConfig={
            showMore ? { showMore: true, numItemsAboveFold } : undefined
          }
          excludeArticleId={parseInt(article.id, 10)}
        />
      </Stack>
    </BedrockImpression>
  );

  return shouldTriggerLegacyImpressions ? (
    <ElementImpression
      id="article-client-related-links"
      elementName="RelatedLinks"
      sectionCategory="sidebar"
    >
      {children}
    </ElementImpression>
  ) : (
    children
  );
};

RelatedLinks.defaultProps = {
  showMore: false,
  numItemsAboveFold: 3,
  maxItems: 4,
};

RelatedLinks.fragment = gql`
  fragment RelatedLinks on Article {
    id
    articleType
    relatedLinks {
      ...BlockGroup
    }
    primaryCategory {
      name
      link
    }
  }
  ${BlockGroup.fragment}
`;

RelatedLinks.shouldShow = (article) => {
  const articleTypeConfig = getArticleTypeConfig(article?.articleType);
  const blockGroup = article?.relatedLinks;
  return _.get(articleTypeConfig, 'hasRelatedLinks', false) && !!blockGroup;
};

export default RelatedLinks;
